import { AnimationName, IAnimationData, ICommonStyles, IFontData, ILinkFontData } from "@pageBuilder/typings";
import { StyleManagerBase } from "src/shared/style-managers/style-manager-base";
import { ICssPropertiesObject } from "../models/css-property";
import { IButtonStyles, IHoveredButtonStyles } from "../typings/button-styles";
import { DEFAULT_COMMON_STYLES } from "../typings/index";
import { StringUtil } from "../utils/string-util";
import { PageStyleManager } from "src/shared/style-managers/page-style-manager";

const SiteDesignVariables = {
    BASE_FONT_SIZE: "--site-design-base-font-size",
    BASE_FONT_SIZE_NUMBER: "--site-design-base-font-size-number",
    SECTION_MAX_WIDTH: "--site-design-section-max-width",
    ANIMATION_NAME: "--site-design-widget-animation-name",
    ANIMATION_DURATION: "--site-design-widget-animation-duration",

    FONT: {
        SIZE_NUM_POSTFIX: "size-num",
        COLOR_POSTFIX: "color",
        WEIGHT_POSTFIX: "weight",
        FAMILY_POSTFIX: "family",
        LINE_HEIGHT_POSTFIX: "line-height",
        LETTER_SPACING_POSTFIX: "letter-spacing",
        TEXT_INDENT_POSTFIX: "text-indent",
        PADDING_INLINE_START_POSTFIX: "padding-inline-start",
    },

    LINK: {
        HOVERED_COLOR_POSTFIX: "hovered-color",
        VISITED_COLOR_POSTFIX: "visited-color",
    },

    FONT_PREFIXES: {
        P: "--site-design-p-font",
        H1: "--site-design-h1-font",
        H2: "--site-design-h2-font",
        H3: "--site-design-h3-font",
        H4: "--site-design-h4-font",
        H5: "--site-design-h5-font",
        NAV: "--site-design-nav-font",
        TH: "--site-design-th-font",
        TD: "--site-design-td-font",
        LIST: "--site-design-list-font",
        LINK: "--site-design-link-font",
        HEADER_NAVIGATION: "--site-design-header-navigation-font",
    },

    BUTTON: {
        FONT_POSTFIX: "font",
        FILLING_COLOR_POSTFIX: "filling-color",
        BORDER_COLOR_POSTFIX: "border-color",
        HOVERED_TEXT_COLOR_POSTFIX: "hovered-text-color",
        HOVERED_FILLING_COLOR_POSTFIX: "hovered-filling-color",
        HOVERED_BORDER_COLOR_POSTFIX: "hovered-border-color",
    },

    PRIMARY_BUTTON_PREFIX: "--site-design-primary-button",
    SECONDARY_BUTTON_PREFIX: "--site-design-secondary-button",
} as const;

export class SiteDesignStylesService {

    private readonly style: HTMLStyleElement;
    private readonly styleManager: StyleManagerBase;

    private constructor(styleManager: StyleManagerBase) {
        this.styleManager = styleManager;
        this.style = styleManager.CommonStyle;
    }

    public static create(styleManager: StyleManagerBase): SiteDesignStylesService {
        return new SiteDesignStylesService(styleManager);
    }

    public static createByDoc(doc: Document): SiteDesignStylesService {
        const styleManager = PageStyleManager.create(doc);
        return new SiteDesignStylesService(styleManager);
    }

    public getStyles(): ICommonStyles {
        return {
            maxWidthInPx: this.getSectionsCommonWidth(DEFAULT_COMMON_STYLES.maxWidthInPx),
            baseFontSize: this.getBaseFontSize(DEFAULT_COMMON_STYLES.baseFontSize),
            animation: this.getAnimation(DEFAULT_COMMON_STYLES.animation),
            headerNavigationFont: this.getFont(SiteDesignVariables.FONT_PREFIXES.HEADER_NAVIGATION, DEFAULT_COMMON_STYLES.headerNavigationFont),
            paragraphFont: this.getFont(SiteDesignVariables.FONT_PREFIXES.P, DEFAULT_COMMON_STYLES.paragraphFont),
            tableHeaderFont: this.getFont(SiteDesignVariables.FONT_PREFIXES.TH, DEFAULT_COMMON_STYLES.tableHeaderFont),
            tableDataFont: this.getFont(SiteDesignVariables.FONT_PREFIXES.TD, DEFAULT_COMMON_STYLES.tableDataFont),
            heading1Font: this.getFont(SiteDesignVariables.FONT_PREFIXES.H1, DEFAULT_COMMON_STYLES.heading1Font),
            heading2Font: this.getFont(SiteDesignVariables.FONT_PREFIXES.H2, DEFAULT_COMMON_STYLES.heading2Font),
            heading3Font: this.getFont(SiteDesignVariables.FONT_PREFIXES.H3, DEFAULT_COMMON_STYLES.heading3Font),
            heading4Font: this.getFont(SiteDesignVariables.FONT_PREFIXES.H4, DEFAULT_COMMON_STYLES.heading4Font),
            subheadingFont: this.getFont(SiteDesignVariables.FONT_PREFIXES.H5, DEFAULT_COMMON_STYLES.subheadingFont),
            navigationFont: this.getFont(SiteDesignVariables.FONT_PREFIXES.NAV, DEFAULT_COMMON_STYLES.navigationFont),
            listFont: this.getFont(SiteDesignVariables.FONT_PREFIXES.LIST, DEFAULT_COMMON_STYLES.listFont),
            linkStyles: this.getLinkFont(SiteDesignVariables.FONT_PREFIXES.LINK, DEFAULT_COMMON_STYLES.linkStyles),
            primaryButtonStyles: this.getButtonStyles(SiteDesignVariables.PRIMARY_BUTTON_PREFIX, DEFAULT_COMMON_STYLES.primaryButtonStyles),
            secondaryButtonStyles: this.getButtonStyles(SiteDesignVariables.SECONDARY_BUTTON_PREFIX, DEFAULT_COMMON_STYLES.secondaryButtonStyles),
            primaryHoveredButtonStyles: this.getHoveredButtonStyles(SiteDesignVariables.PRIMARY_BUTTON_PREFIX, DEFAULT_COMMON_STYLES.primaryHoveredButtonStyles),
            secondaryHoveredButtonStyles: this.getHoveredButtonStyles(SiteDesignVariables.SECONDARY_BUTTON_PREFIX, DEFAULT_COMMON_STYLES.secondaryHoveredButtonStyles),
        };
    }

    private getSectionsCommonWidth(defaultWidth: number): number {
        const cssValue = this.getCssVariableValue(SiteDesignVariables.SECTION_MAX_WIDTH);
        return StringUtil.getNumberFromStr(cssValue, defaultWidth);
    }

    private getBaseFontSize(defaultSize: number): number {
        const cssValue = this.getCssVariableValue(SiteDesignVariables.BASE_FONT_SIZE);
        return StringUtil.getNumberFromStr(cssValue, defaultSize);
    }

    private getAnimation(defaultAnimation: IAnimationData): IAnimationData {
        const animationName = this.getCssVariableValue(SiteDesignVariables.ANIMATION_NAME);
        const animationDuration = this.getCssVariableValue(SiteDesignVariables.ANIMATION_DURATION);

        return {
            name: animationName as AnimationName ?? defaultAnimation.name,
            duration: StringUtil.getNumberFromStr(animationDuration, defaultAnimation.duration)
        };
    }

    private getFont(cssVariablePrefix: string, defaultFont: IFontData): IFontData {
        return {
            size: this.getCssVariableValue(`${cssVariablePrefix}-${SiteDesignVariables.FONT.SIZE_NUM_POSTFIX}`) ?? String(parseFloat(defaultFont.size ?? "0")),
            color: this.getCssVariableValue(`${cssVariablePrefix}-${SiteDesignVariables.FONT.COLOR_POSTFIX}`) ?? defaultFont.color,
            weight: this.getCssVariableValue(`${cssVariablePrefix}-${SiteDesignVariables.FONT.WEIGHT_POSTFIX}`) ?? defaultFont.weight,
            family: this.getCssVariableValue(`${cssVariablePrefix}-${SiteDesignVariables.FONT.FAMILY_POSTFIX}`) ?? defaultFont.family,
            lineHeight: this.getCssVariableValue(`${cssVariablePrefix}-${SiteDesignVariables.FONT.LINE_HEIGHT_POSTFIX}`) ?? defaultFont.lineHeight,
            letterSpacing: this.getCssVariableValue(`${cssVariablePrefix}-${SiteDesignVariables.FONT.LETTER_SPACING_POSTFIX}`) ?? defaultFont.letterSpacing,
            textIndent: this.getCssVariableValue(`${cssVariablePrefix}-${SiteDesignVariables.FONT.TEXT_INDENT_POSTFIX}`) ?? defaultFont.textIndent,
            paddingInlineStart: this.getCssVariableValue(`${cssVariablePrefix}-${SiteDesignVariables.FONT.PADDING_INLINE_START_POSTFIX}`) ?? defaultFont.paddingInlineStart,
        };
    }

    private getLinkFont(cssVariablePrefix: string, defaultFont: ILinkFontData): ILinkFontData {
        return {
            font: this.getFont(cssVariablePrefix, defaultFont.font),
            hoveredColor: this.getCssVariableValue(`${cssVariablePrefix}-${SiteDesignVariables.LINK.HOVERED_COLOR_POSTFIX}`) ?? defaultFont.hoveredColor,
            visitedColor: this.getCssVariableValue(`${cssVariablePrefix}-${SiteDesignVariables.LINK.VISITED_COLOR_POSTFIX}`) ?? defaultFont.visitedColor,
        };
    }

    private getButtonStyles(cssVariablePrefix: string, defaultStyles: IButtonStyles): IButtonStyles {
        return {
            font: this.getFont(`${cssVariablePrefix}-${SiteDesignVariables.BUTTON.FONT_POSTFIX}`, defaultStyles.font),
            fillingColor: this.getCssVariableValue(`${cssVariablePrefix}-${SiteDesignVariables.BUTTON.FILLING_COLOR_POSTFIX}`) ?? defaultStyles.fillingColor,
            borderColor: this.getCssVariableValue(`${cssVariablePrefix}-${SiteDesignVariables.BUTTON.BORDER_COLOR_POSTFIX}`) ?? defaultStyles.borderColor
        };
    }

    private getHoveredButtonStyles(cssVariablePrefix: string, defaultStyles: IHoveredButtonStyles): IHoveredButtonStyles {
        return {
            fillingColor: this.getCssVariableValue(`${cssVariablePrefix}-${SiteDesignVariables.BUTTON.HOVERED_FILLING_COLOR_POSTFIX}`) ?? defaultStyles.fillingColor,
            borderColor: this.getCssVariableValue(`${cssVariablePrefix}-${SiteDesignVariables.BUTTON.HOVERED_BORDER_COLOR_POSTFIX}`) ?? defaultStyles.borderColor,
            textColor: this.getCssVariableValue(`${cssVariablePrefix}-${SiteDesignVariables.BUTTON.HOVERED_TEXT_COLOR_POSTFIX}`) ?? defaultStyles.textColor
        };
    }

    private getCssVariableValue(variableName: string): string | null {
        const cssText = this.style.innerHTML;
        const regex = new RegExp(`${variableName}:\\s*([^;]+);`);
        const match = cssText.match(regex);
        return match ? match[1].trim() : null;
    }

    public applyStyles(curStyles: ICommonStyles | null | undefined): void {
        const rootVariables: ICssPropertiesObject = {};

        this.addCssVariablesForWidth(rootVariables, curStyles?.maxWidthInPx, DEFAULT_COMMON_STYLES.maxWidthInPx);
        this.addCssVariablesForAnimation(rootVariables, curStyles?.animation, DEFAULT_COMMON_STYLES.animation);
        this.addCssVariablesForBaseFontSize(rootVariables, curStyles?.baseFontSize, DEFAULT_COMMON_STYLES.baseFontSize);
        this.addCssVariablesForFont(rootVariables, SiteDesignVariables.FONT_PREFIXES.P, curStyles?.paragraphFont, DEFAULT_COMMON_STYLES.paragraphFont);
        this.addCssVariablesForFont(rootVariables, SiteDesignVariables.FONT_PREFIXES.NAV, curStyles?.navigationFont, DEFAULT_COMMON_STYLES.navigationFont);
        this.addCssVariablesForFont(rootVariables, SiteDesignVariables.FONT_PREFIXES.TH, curStyles?.tableHeaderFont, DEFAULT_COMMON_STYLES.tableHeaderFont);
        this.addCssVariablesForFont(rootVariables, SiteDesignVariables.FONT_PREFIXES.TD, curStyles?.tableDataFont, DEFAULT_COMMON_STYLES.tableDataFont);
        this.addCssVariablesForFont(rootVariables, SiteDesignVariables.FONT_PREFIXES.LIST, curStyles?.listFont, DEFAULT_COMMON_STYLES.listFont);
        this.addCssVariablesForFont(rootVariables, SiteDesignVariables.FONT_PREFIXES.H1, curStyles?.heading1Font, DEFAULT_COMMON_STYLES.heading1Font);
        this.addCssVariablesForFont(rootVariables, SiteDesignVariables.FONT_PREFIXES.H2, curStyles?.heading2Font, DEFAULT_COMMON_STYLES.heading2Font);
        this.addCssVariablesForFont(rootVariables, SiteDesignVariables.FONT_PREFIXES.H3, curStyles?.heading3Font, DEFAULT_COMMON_STYLES.heading3Font);
        this.addCssVariablesForFont(rootVariables, SiteDesignVariables.FONT_PREFIXES.H4, curStyles?.heading4Font, DEFAULT_COMMON_STYLES.heading4Font);
        this.addCssVariablesForFont(rootVariables, SiteDesignVariables.FONT_PREFIXES.H5, curStyles?.subheadingFont, DEFAULT_COMMON_STYLES.subheadingFont);
        this.addCssVariablesForFont(rootVariables, SiteDesignVariables.FONT_PREFIXES.HEADER_NAVIGATION, curStyles?.headerNavigationFont, DEFAULT_COMMON_STYLES.headerNavigationFont);
        this.addCssVariablesForLinkFont(rootVariables, SiteDesignVariables.FONT_PREFIXES.LINK, curStyles?.linkStyles, DEFAULT_COMMON_STYLES.linkStyles);
        this.addCssVariablesForButtonStyles(rootVariables, SiteDesignVariables.PRIMARY_BUTTON_PREFIX, curStyles?.primaryButtonStyles, DEFAULT_COMMON_STYLES.primaryButtonStyles);
        this.addCssVariablesForButtonStyles(rootVariables, SiteDesignVariables.SECONDARY_BUTTON_PREFIX, curStyles?.secondaryButtonStyles, DEFAULT_COMMON_STYLES.secondaryButtonStyles);
        this.addCssVariablesForHoverButtonStyles(rootVariables, SiteDesignVariables.PRIMARY_BUTTON_PREFIX, curStyles?.primaryHoveredButtonStyles, DEFAULT_COMMON_STYLES.primaryHoveredButtonStyles);
        this.addCssVariablesForHoverButtonStyles(rootVariables, SiteDesignVariables.SECONDARY_BUTTON_PREFIX, curStyles?.secondaryHoveredButtonStyles, DEFAULT_COMMON_STYLES.secondaryHoveredButtonStyles);

        this.styleManager.setRootCssVariablesObjectToStyleTag(this.style, rootVariables);
    }

    private addCssVariablesForWidth(rootVariables: ICssPropertiesObject, width: number | null | undefined, defaultWidth: number): void {
        rootVariables[SiteDesignVariables.SECTION_MAX_WIDTH] = `${width ?? defaultWidth}px`;
    }

    private addCssVariablesForAnimation(rootVariables: ICssPropertiesObject, animation: IAnimationData | null | undefined, defaultAnimation: IAnimationData): void {
        rootVariables[SiteDesignVariables.ANIMATION_NAME] = animation?.name ?? defaultAnimation.name;
        rootVariables[SiteDesignVariables.ANIMATION_DURATION] = `${animation?.duration ?? defaultAnimation.duration}s`;
    }

    private addCssVariablesForBaseFontSize(rootVariables: ICssPropertiesObject, size: number | null | undefined, defaultSize: number): void {
        rootVariables[SiteDesignVariables.BASE_FONT_SIZE] = `${size ?? defaultSize}px`;
        rootVariables[SiteDesignVariables.BASE_FONT_SIZE_NUMBER] = `${size ?? defaultSize}`;
    }

    private addCssVariablesForFont(rootVariables: ICssPropertiesObject, variablePrefix: string, font: IFontData | null | undefined, defaultFont: IFontData): void {
        rootVariables[`${variablePrefix}-${SiteDesignVariables.FONT.SIZE_NUM_POSTFIX}`] = String(parseFloat(font?.size ?? defaultFont.size ?? "0"));
        rootVariables[`${variablePrefix}-${SiteDesignVariables.FONT.COLOR_POSTFIX}`] = font?.color ?? defaultFont.color;
        rootVariables[`${variablePrefix}-${SiteDesignVariables.FONT.WEIGHT_POSTFIX}`] = font?.weight ?? defaultFont.weight;
        rootVariables[`${variablePrefix}-${SiteDesignVariables.FONT.FAMILY_POSTFIX}`] = font?.family ?? defaultFont.family;
        rootVariables[`${variablePrefix}-${SiteDesignVariables.FONT.LETTER_SPACING_POSTFIX}`] = font?.letterSpacing ?? defaultFont.letterSpacing;
        rootVariables[`${variablePrefix}-${SiteDesignVariables.FONT.LINE_HEIGHT_POSTFIX}`] = font?.lineHeight ?? defaultFont.lineHeight;
        rootVariables[`${variablePrefix}-${SiteDesignVariables.FONT.TEXT_INDENT_POSTFIX}`] = font?.textIndent ?? defaultFont.textIndent;
        rootVariables[`${variablePrefix}-${SiteDesignVariables.FONT.PADDING_INLINE_START_POSTFIX}`] = font?.paddingInlineStart ?? defaultFont.paddingInlineStart;
    }

    private addCssVariablesForLinkFont(rootVariables: ICssPropertiesObject, variablePrefix: string, font: ILinkFontData | null | undefined, defaultFont: ILinkFontData): void {
        rootVariables[`${variablePrefix}-${SiteDesignVariables.LINK.HOVERED_COLOR_POSTFIX}`] = font?.hoveredColor ?? defaultFont.hoveredColor;
        rootVariables[`${variablePrefix}-${SiteDesignVariables.LINK.VISITED_COLOR_POSTFIX}`] = font?.visitedColor ?? defaultFont.visitedColor;

        this.addCssVariablesForFont(rootVariables, variablePrefix, font?.font, defaultFont.font);
    }

    private addCssVariablesForButtonStyles(rootVariables: ICssPropertiesObject, variablePrefix: string, styles: IButtonStyles | null | undefined, defaultStyles: IButtonStyles): void {
        rootVariables[`${variablePrefix}-${SiteDesignVariables.BUTTON.FILLING_COLOR_POSTFIX}`] = styles?.fillingColor ?? defaultStyles.fillingColor;
        rootVariables[`${variablePrefix}-${SiteDesignVariables.BUTTON.BORDER_COLOR_POSTFIX}`] = styles?.borderColor ?? defaultStyles.borderColor;

        this.addCssVariablesForFont(rootVariables, `${variablePrefix}-${SiteDesignVariables.BUTTON.FONT_POSTFIX}`, styles?.font, defaultStyles.font);
    }

    private addCssVariablesForHoverButtonStyles(rootVariables: ICssPropertiesObject, variablePrefix: string, styles: IHoveredButtonStyles | null | undefined, defaultStyles: IHoveredButtonStyles): void {
        rootVariables[`${variablePrefix}-${SiteDesignVariables.BUTTON.HOVERED_FILLING_COLOR_POSTFIX}`] = styles?.fillingColor ?? defaultStyles.fillingColor;
        rootVariables[`${variablePrefix}-${SiteDesignVariables.BUTTON.HOVERED_BORDER_COLOR_POSTFIX}`] = styles?.borderColor ?? defaultStyles.borderColor;
        rootVariables[`${variablePrefix}-${SiteDesignVariables.BUTTON.HOVERED_TEXT_COLOR_POSTFIX}`] = styles?.textColor ?? defaultStyles.textColor;
    }

}
